import type React from "react"
import { CalendarBlank, CaretRight, Equals, Warehouse } from "@phosphor-icons/react"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "~/components/reusable/hovercard/HoverCard"
import HoverCardRow from "~/components/reusable/hovercard/HoverCardRow"
import HoverCardRowWrapper from "~/components/reusable/hovercard/HoverCardRowWrapper"
import { formatNumber } from "~/utils/formatting"

export type WarehouseBreakdownEntry = {
  id: string
  name: string
  value: number
}

export type BucketBreakdown = {
  title: string
  rows: { id: string; label: string; value: number; current?: boolean }[]
  note?: string
}

type Props = {
  label: string
  total: number
  perWarehouse: WarehouseBreakdownEntry[]
  buckets?: BucketBreakdown
  formatValue?: (n: number) => string
  unit?: string
  isSingleWarehouse: boolean
  /** Footer note under the breakdown. Newlines are preserved, so it can spell out a calculation. */
  caption?: string
  trigger: React.ReactNode
  headerIcon?: React.ReactNode
  rowIcon?: React.ReactNode
  // When set, each per-warehouse row renders as a clickable button. Used by drill-down callers
  // (e.g. BuyTable's incoming-stock cells) to open a per-warehouse dialog.
  onRowClick?: (id: string) => void
  /** Hide the label + total header block. Used on NOOS Supply Plan to reduce tooltip size. */
  hideHeader?: boolean
  /** Hide the dashed "Total" summary row. Use when summing the metric makes no semantic sense (e.g. weeks-on-hand). */
  hideTotal?: boolean
}

export function WarehouseBreakdownTooltip({
  label,
  total,
  perWarehouse,
  buckets,
  formatValue,
  unit,
  isSingleWarehouse,
  caption,
  trigger,
  headerIcon,
  rowIcon,
  onRowClick,
  hideHeader,
  hideTotal,
}: Props) {
  const fmt = formatValue ?? formatNumber
  const suffix = unit ? ` ${unit}` : ""

  return (
    <HoverCard>
      <HoverCardTrigger className="flex h-full w-full items-center">{trigger}</HoverCardTrigger>

      <HoverCardContent className="w-80 p-0" align="start">
        {!hideHeader && (
          <div className="px-4 py-3 border-b border-base bg-base">
            <div className="flex items-center justify-between gap-2">
              {headerIcon}
              <span className="label-base text-fg-base flex-1">{label}</span>
            </div>
            <div className="text-2xl font-semibold mt-1 text-fg-base">
              {fmt(total)}
              {suffix}
              {!isSingleWarehouse && perWarehouse.length > 1 && (
                <span className="paragraph-sm text-fg-subtle ml-1">(total)</span>
              )}
            </div>
          </div>
        )}

        <HoverCardRowWrapper>
          <div className="max-h-[700px] overflow-y-auto">
            {perWarehouse.map((wh) =>
              onRowClick ? (
                <button
                  key={wh.id}
                  type="button"
                  onClick={() => onRowClick(wh.id)}
                  className="flex w-full items-center justify-between gap-3 paragraph-sm px-4 py-1.5 my-1.5 rounded-[6px] text-left hover:bg-subtle focus-visible:bg-subtle focus-visible:outline-hidden cursor-pointer"
                >
                  <span className="flex items-center gap-2 text-fg-subtle min-w-0">
                    <span className="shrink-0">{rowIcon ?? <Warehouse />}</span>
                    <span className="truncate">{wh.name}</span>
                  </span>
                  <span className="font-medium text-fg-base flex items-center gap-1 shrink-0">
                    {`${fmt(wh.value)}${suffix}`}
                    <CaretRight className="text-fg-muted" />
                  </span>
                </button>
              ) : (
                <HoverCardRow
                  key={wh.id}
                  icon={rowIcon ?? <Warehouse />}
                  label={wh.name}
                  value={`${fmt(wh.value)}${suffix}`}
                />
              ),
            )}
            {!hideTotal && !isSingleWarehouse && perWarehouse.length > 1 && (
              <>
                <hr className="border-t border-dashed border-base" />
                <HoverCardRow icon={<Equals />} label="Total" value={`${fmt(total)}${suffix}`} />
              </>
            )}
          </div>
        </HoverCardRowWrapper>

        {buckets && (
          <div className="border-t border-base">
            <p className="paragraph-sm text-fg-subtle px-4 pt-3">{buckets.title}</p>
            <HoverCardRowWrapper>
              {buckets.rows.map((bucket) => (
                <HoverCardRow
                  key={bucket.id}
                  icon={<CalendarBlank />}
                  label={bucket.label}
                  value={`${fmt(bucket.value)}${suffix}`}
                  className={bucket.current ? undefined : "font-normal text-fg-subtle"}
                  valueExtra={
                    bucket.current ? (
                      <span className="text-xs text-fg-muted">this column</span>
                    ) : undefined
                  }
                />
              ))}
            </HoverCardRowWrapper>
            {buckets.note && <p className="paragraph-sm text-fg-muted px-4 pb-3">{buckets.note}</p>}
          </div>
        )}

        {caption && (
          <div className="px-4 py-3 border-t border-base bg-subtle text-fg-subtle">
            <p className="paragraph-sm whitespace-pre-line">{caption}</p>
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  )
}
